import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { List } from 'immutable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Subject } from 'rxjs/Subject';
import { AgencyLookupHttpService } from './agency-lookup.http.service';
import { LookupStore } from '../lookup.store';
import { AgencyLookupSummary } from '../../models/agency-lookup-summary';
import { AgencyLookupDetail } from '../../models/agency-lookup-detail';
import { FlashMessagesService } from '../../flash-messages/service/flash.messages.service';
import { LogService } from '../../log/log.service';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';

interface SubscriberCacheEntry {
  value: boolean;
  expiry: number;
}

@Injectable()
export class AgencyLookupStore implements LookupStore<AgencyLookupSummary, AgencyLookupDetail> {
  private mySelectedResults: Subject<AgencyLookupDetail> = new Subject();
  private mySearchResults: BehaviorSubject<List<AgencyLookupSummary>> = new BehaviorSubject(List([]));
  private subscriberCache = new Map<string, BehaviorSubject<SubscriberCacheEntry>>();
  private cacheExpirationTime = 60 * 60 * 1000; // 1 hour in milliseconds

  constructor(private agencyLookupHttpService: AgencyLookupHttpService,
              private logService: LogService,
              private flashMessagesService: FlashMessagesService) {
  }

  get selected(): Observable<AgencyLookupDetail> {
    return this.mySelectedResults.asObservable().share();
  }

  get searchResults(): Observable<List<AgencyLookupSummary>> {
    return this.mySearchResults.asObservable().share();
  }

  clearSearchResults(): void {
    this.mySearchResults.next(List([]));
  }

  search(searchText: string): Observable<List<AgencyLookupSummary>> {
    this.logService.debug(`searchText: ${searchText}`);

    return this.agencyLookupHttpService.search(searchText)
      .catch((error: any) => {
        this.flashMessagesService.warn('MESSAGES.ERROR.AGENCY_LOOKUP_SEARCH', `Error retrieving AgencyLookupSearch: ${error}`);
        return Observable.throw(error);
      })
      .map((res: List<AgencyLookupSummary>) => {
        this.mySearchResults.next(res);
        return res;
      });
  }

  getDetail(summaryRecord: AgencyLookupSummary): Observable<AgencyLookupDetail> {
    this.logService.debug(`summaryRecord: ${JSON.stringify(summaryRecord)}`);

    return this.agencyLookupHttpService.getDetail(summaryRecord)
      .catch((error: any) => {
        this.flashMessagesService.error('MESSAGES.ERROR.AGENCY_LOOKUP_GET_DETAILS', `Error retrieving AgencyLookupDetail: ${error}`);
        return Observable.throw(error);
      })
      .map((res: AgencyLookupDetail) => {
        this.mySelectedResults.next(res);
        return res;
      });
  }

  isSubscriberAgency(arcIata: string): Observable<boolean> {
    this.logService.debug(`checkSubscriberAgency - iata: ${arcIata}`);

    let cachedEntry = this.subscriberCache.get(arcIata);

    const entryExists = !!cachedEntry;
    const entryExpired = !!cachedEntry && !!cachedEntry.value && cachedEntry.value.expiry < Date.now();

    if (!entryExists) {
      cachedEntry = new BehaviorSubject<SubscriberCacheEntry | null>(null);
      this.subscriberCache.set(arcIata, cachedEntry);
    }

    if (!entryExists || entryExpired) {
      this.agencyLookupHttpService.isSubscriberAgency(arcIata).pipe(
        take(1),
        tap(
          (isSubscriber: boolean) => {
            const newEntry = { value: isSubscriber, expiry: Date.now() + this.cacheExpirationTime };
            cachedEntry!.next(newEntry);
          }
        ),
        catchError(
          (error: any) => {
            this.flashMessagesService.error('MESSAGES.ERROR.SUBSCRIBER_AGENCY_VERIFICATION', `Error checking SubscriberAgency: ${error}`);
            return Observable.throw(error);
          }
        )).subscribe();
    }

    return cachedEntry.asObservable().pipe(
      filter((entry: SubscriberCacheEntry | null) => !!entry),
      map((entry: SubscriberCacheEntry) => entry.value)
    );
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/shared/lookup/agency-lookup/agency-lookup.store.ts